import { FC } from 'react';
import styled from 'styled-components';
import { Alert, Button, Chip, CircularProgress, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { Field, useField } from 'formik';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';

import { InputField } from '../../../fields';
import { ApiError } from '../../../errors';
import { api } from '../../../../api';
import { COUPON_DISPLAY_NAME_MAX_LENGTH } from '../../../../config';
import { getIsActive, getIsReachLimit } from '../../../../lib/utils/coupon';
import { useAppSelector } from '../../../../lib';
import { configSelectors } from '../../../../store';
import { CouponInCampain, CouponWithUsages } from '../../../../types/coupon';
import { useTranslation } from 'react-i18next';

type CouponSettingsProps = {
  onCloseModal: () => void;
  couponIndex: number;
};

export const CouponSettings: FC<CouponSettingsProps> = ({ onCloseModal, couponIndex }) => {
  const wixCouponIdField = `campaign.wixCoupons[${couponIndex}].uuid`;
  const descriptionField = `campaign.wixCoupons[${couponIndex}].description`;
  const displayNameField = `campaign.wixCoupons[${couponIndex}].displayName`;

  const { t } = useTranslation();

  const [field] = useField<string>(displayNameField);
  const [wixCouponId] = useField<string>(wixCouponIdField);

  return (
    <DialogContentWrapper>
      <DialogTitle>{t('pages.coupons.additionalSettings')}</DialogTitle>

      <DialogContent>
        <Field
          name={displayNameField}
          component={InputField}
          submitOnBlur={false}
          label={`${t('pages.coupons.displayName')} ${field.value?.length || 0}/${COUPON_DISPLAY_NAME_MAX_LENGTH}`}
          className="wix-coupon-display-name"
          inputProps={{
            maxLength: COUPON_DISPLAY_NAME_MAX_LENGTH,
          }}
          disabled={!wixCouponId.value}
        />

        <Field
          name={descriptionField}
          component={InputField}
          submitOnBlur={false}
          label={t('pages.coupons.description')}
          multiline
          rows={5}
          className="wix-coupon-description"
          disabled={!wixCouponId.value}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onCloseModal}>Ok</Button>
      </DialogActions>
    </DialogContentWrapper>
  );
};

type CouponDeleteProps = {
  onCloseModal: () => void;
  onDelete: () => void;
};

export const CouponDelete: FC<CouponDeleteProps> = ({ onCloseModal, onDelete }) => {
  const { t } = useTranslation();

  return (
    <DialogContentWrapper>
      <DialogTitle>{t('pages.coupons.deleteConfirmShort')}</DialogTitle>

      <DialogActions>
        <Button onClick={onCloseModal}>{t('buttons.cancel')}</Button>

        <Button onClick={onDelete}>{t('buttons.delete')}</Button>
      </DialogActions>
    </DialogContentWrapper>
  );
};

type PlatesSectionProps = {
  apiError?: FetchBaseQueryError | SerializedError | undefined;
  validationError?: string | Record<string, unknown>;
};

export const PlatesSection: FC<PlatesSectionProps> = ({ apiError, validationError }) => {
  const [refetchWixCoupons, wixCoupons] = api.useLazyGetCouponsQuery();

  const isWix = useAppSelector(configSelectors.isWix);

  const showValidationError = Boolean(validationError && typeof validationError === 'string');
  const showCouponsSuggestion = !isWix && !showValidationError;

  return (
    <PlatesSectionWrapper>
      {isWix && (
        <CreateWixCouponsInfo severity="info" variant="filled">
          <CreateWixCouponsInfoTitle>
            To begin, create coupons in site settings left menu: Marketing & SEO {'>'} Coupons
          </CreateWixCouponsInfoTitle>

          <Button
            size="small"
            onClick={() => {
              refetchWixCoupons();
            }}
            disabled={wixCoupons.isFetching}
            className="refresh-wix-coupons-button"
          >
            {wixCoupons.isFetching ? <CircularProgress size={22.5} /> : 'Reload wix coupons'}
          </Button>
        </CreateWixCouponsInfo>
      )}

      {apiError && (
        <ApiErrorPlate severity="error" variant="filled">
          <ApiError error={apiError} />
        </ApiErrorPlate>
      )}

      {showValidationError && (
        <SumErrorPlate severity="error" variant="filled">
          {validationError}
        </SumErrorPlate>
      )}

      {showCouponsSuggestion && (
        <CreateWixCouponsInfo severity="info" variant="filled">
          Создавать и редактировать купоны вы можете в разделе «Купоны» (слева в меню сайта)
        </CreateWixCouponsInfo>
      )}
    </PlatesSectionWrapper>
  );
};

type CouponStatusPlateProps = {
  userCoupon?: CouponWithUsages;
  coupon: CouponInCampain;
};

export const CouponStatusPlate: FC<CouponStatusPlateProps> = ({ coupon, userCoupon }) => {
  const { t } = useTranslation();

  if (coupon.uuid && !userCoupon) {
    return <Chip label={t('pages.coupons.statuses.notFound')} size="small" color="error" />;
  }

  if (!userCoupon) {
    return <Chip label={t('pages.coupons.statuses.notAssigned')} size="small" color="default" />;
  }

  if (getIsReachLimit(userCoupon)) {
    return <Chip label={t('pages.coupons.statuses.reachLimit')} size="small" color="warning" />;
  }

  if (!getIsActive(userCoupon)) {
    return <Chip label={t('pages.coupons.statuses.notActive')} size="small" color="warning" />;
  }

  return <Chip label={t('pages.coupons.statuses.active')} size="small" color="success" />;
};

const DialogContentWrapper = styled.div`
  min-width: 400px;

  .wix-coupon-display-name {
    width: 100%;
    margin: 8px 0 16px;
  }

  .wix-coupon-description {
    width: 100%;
  }
`;

const PlatesSectionWrapper = styled.div`
  margin-bottom: 28px;
`;

const ApiErrorPlate = styled(Alert)`
  margin-bottom: 12px;
`;

const SumErrorPlate = styled(Alert)`
  margin-bottom: 12px;
  white-space: pre-wrap;
`;

const CreateWixCouponsInfo = styled(Alert)`
  display: flex;
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
  margin-bottom: 12px;

  .MuiAlert-message {
    display: flex;
    width: 100%;
    justify-content: space-between;
    padding: 4px 0;
  }

  .refresh-wix-coupons-button {
    min-width: 163px;
  }
`;

const CreateWixCouponsInfoTitle = styled.div`
  display: flex;
  align-items: center;
`;
